import { useUnsavedPromptInModal } from '@common/UnsavedPromptInModal.hooks';
import { Button, ComponentType, LoadingButton, ModalBodyExtras, useForm, useModal } from '@gcl/react-core';
import * as React from 'react';
import { useReassignCheckerDesignModification } from '@designModification/designModification.queries';
import { EDesignModificationFormKeys, IProjectDesignModification } from '@designModification/designModification.models';
import { DesignModificationReassignCheckerForm } from './DesignModificationReassignCheckerForm';

export interface IUseDesignModificationReassignCheckerModalProps {}

export interface IRenderDesignModificationReassignCheckerModalProps
  extends IUseDesignModificationReassignCheckerModalProps {
  projectId: number;
  designModification?: IProjectDesignModification;
}

export const useDesignModificationReassignCheckerModal = ({}: // onConfirm,
IUseDesignModificationReassignCheckerModalProps) => {
  const { openPortal, closePortal, renderModal } = useModal();

  const mutationReassignChecker = useReassignCheckerDesignModification();
  const {
    mutate: mutateReassignChecker,
    isLoading: isLoadingReassignChecker,
    isError: isErrorReassignChecker,
    isSuccess: isSuccessReassignChecker,
  } = mutationReassignChecker;

  const renderReassignCheckerModal = ({
    projectId,
    designModification,
  }: IRenderDesignModificationReassignCheckerModalProps) => {
    const { isFormDirty, setIsFormDirty, confirmUnsavedPrompt } = useUnsavedPromptInModal();
    const useFormMethods = useForm();
    const {
      formState: { isDirty },
    } = useFormMethods;

    React.useEffect(() => {
      setIsFormDirty(isDirty);
    }, [isDirty]);

    React.useEffect(() => {
      if (isSuccessReassignChecker) closePortal();
    }, [isSuccessReassignChecker]);

    const onSubmit = (data: any) => {
      mutateReassignChecker({
        id: designModification?.id ?? -1,
        projectId,
        rowVersion: designModification?.rowVersion,
        nominatedCheckerCompanyTeamId: data[EDesignModificationFormKeys.NOMINATED_CHECKER].id,
      });
    };

    return renderModal({
      onHide: () => {
        useFormMethods.reset({});
      },
      backdrop: isFormDirty ? 'static' : undefined,
      large: true,
      title: 'Re-assign Checker',
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <DesignModificationReassignCheckerForm
            projectId={projectId}
            designModification={designModification}
            useFormMethods={useFormMethods}
            mutationReassignChecker={mutationReassignChecker}
          />
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <>
            <LoadingButton
              type={ComponentType.Primary}
              onClick={() => {
                useFormMethods.handleSubmit(onSubmit)();
              }}
              isLoading={isLoadingReassignChecker}
              isError={isErrorReassignChecker}
              isSuccess={isSuccessReassignChecker}
              timeToResetSuccessState={3000}
              timeToResetErrorState={5000}
              prefixIconName="FolderCheck"
            >
              Submit
            </LoadingButton>
            <Button
              prefixIconName="ChevronLeft"
              type={ComponentType.Secondary}
              onClick={() => {
                if (confirmUnsavedPrompt() && onHide) {
                  onHide();
                }
              }}
              style={{ margin: '10px' }}
            >
              Cancel
            </Button>
          </>
        );
      },
    });
  };
  return {
    openPortal,
    closePortal,
    renderModal: renderReassignCheckerModal,
  };
};
